import type { Countries, Country } from '@ps/web-core-intl';
import type { SelectOption } from '@ps/web-shared-components';
import { getCountries, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { isEmpty, isNull, isUndefined } from 'lodash';

import countriesList from '@swf/generated/countries.json';

interface DialingCode {
  text: string;
  value: string;
}
interface CountriesCodeList {
  value: string;
  text: string;
  numericValue: string;
  data: string;
}

interface DialingCodeSelectOption extends SelectOption {
  numericValue: string;
}

const countriesAll = countriesList as Countries;

const countriesListLibrary = getCountries();

export const countries = countriesAll.filter(({ value }) =>
  countriesListLibrary.includes(value as CountryCode)
) as Countries;

export const getCountryCode = (lookupCountry: string) => {
  const country = countries.find(({ value }) => value === lookupCountry) as Country;
  const dialingCode = countriesListLibrary.includes(lookupCountry as CountryCode)
    ? getCountryCallingCode(lookupCountry as CountryCode)
    : undefined;

  if (isUndefined(dialingCode) || isUndefined(country?.value)) return;

  return {
    dialingCode,
    text: `+${dialingCode}`,
    value: country.value
  };
};

export const countryInitialName = (phone: string, country: string | undefined) => {
  try {
    return parsePhoneNumber('+' + phone).country;
  } catch {
    return country;
  }
};

export const dialingCodeSelectOptions: DialingCodeSelectOption[] = countries.map(
  ({ value, text }): CountriesCodeList => {
    const code = getCountryCallingCode(value as CountryCode);

    return {
      text: `${text} (${code})`,
      value: `${code} ${text}`,
      numericValue: code,
      data: text
    };
  }
);

export const getInitialOptions = (lookupPhoneNumber: string, lookupCountry: string) => {
  const parsedPhoneNumberCountry =
    countryInitialName(lookupPhoneNumber, lookupCountry) || lookupCountry;
  const country = countries.find(({ value }) => value === parsedPhoneNumberCountry)?.text;
  const dialingCode = dialingCodeSelectOptions.find(
    ({ data: countryName }) => country === countryName
  );
  const phoneNumber = lookupPhoneNumber?.replace(dialingCode?.numericValue as string, '');

  if (isUndefined(dialingCode) || isUndefined(phoneNumber)) return;

  return {
    dialingCode: dialingCode.value as string,
    phoneNumber,
    country
  };
};
export const getCountryShortName = (country: string) =>
  countries.find(({ text }) => text === country)?.value;

export const formatPhoneNumber = (lookupCountry?: string, lookupPhoneNumber?: string) => {
  if (
    isUndefined(lookupCountry) ||
    isUndefined(lookupPhoneNumber) ||
    isNull(lookupPhoneNumber) ||
    isEmpty(lookupPhoneNumber)
  )
    return;

  const code = getCountryCode(lookupCountry)?.text.slice(1);

  if (isUndefined(code) || isUndefined(lookupPhoneNumber)) return;

  return `${code}${lookupPhoneNumber}`;
};

export const countriesCode = countries
  .map(({ text, value }) => {
    const code = getCountryCallingCode(value as CountryCode);
    return {
      value,
      text,
      code
    };
  })
  .filter(({ text }) => !isUndefined(text));

const countryCodeOptions = countries.map(({ value }): DialingCode => {
  const code = getCountryCallingCode(value as CountryCode);
  return {
    text: `+${code}`,
    value: code
  };
});

export const dialingCodeOptions = countryCodeOptions
  .reduce(
    (codes, currentCode) =>
      codes.find(({ value }) => value === currentCode?.value) ? codes : [...codes, currentCode],
    [countryCodeOptions[0]]
  )
  .sort((a, b) => Number(a.value) - Number(b.value));

export const getCountry = (countryCode: string) =>
  countriesAll.find(({ value }) => value === countryCode);

export const dialingCodeSelectOptionsByCountry = (countryCode: string): DialingCodeSelectOption[] =>
  dialingCodeSelectOptions.filter(
    ({ data: countryName }) => getCountry(countryCode)?.text === countryName
  );
